<template>
  <span
    class="time-input-wrapper"
    :class="{
      'wheels-form-error': error,
      'wheels-form-disabled': disabled,
    }"
  >
    <label v-if="label" :for="`time-input-${uuid}`" class="input-label">
      {{ label }}
    </label>

    <div
      class="time-input-field-wrapper wheels-form-field"
      :class="{ 'wheels-form-variant-blue-gray': variant === 'blue-gray' }"
      role="combobox"
      :aria-expanded="isDropdownOpen"
      :aria-haspopup="true"
      :aria-controls="`time-input-dropdown-${uuid}`"
      :aria-invalid="error"
      :aria-disabled="disabled"
      ref="timeInputContainer"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
    >
      <input
        :id="`time-input-${uuid}`"
        ref="timeInputElement"
        type="text"
        class="time-input-field"
        :value="value"
        autocomplete="off"
        readonly
        :placeholder="placeholder || 'Select time'"
        :disabled="disabled"
        :aria-label="placeholder || 'Select time'"
      />
      <div
        v-if="isDropdownOpen"
        class="wheels-form-field__dropdown time-picker-dropdown"
        :class="{ active: isDropdownOpen }"
        :id="`time-input-dropdown-${uuid}`"
        ref="timeInputDropdown"
        role="dialog"
        :aria-label="'Time picker'"
      >
        <WheelsDigitalTimePicker
          ref="digitalTimePicker"
          :value="isoValue"
          @input="handleDigitalPickerChange"
        />
      </div>
    </div>
  </span>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { WheelsDigitalTimePicker } from '../../components'
import { time12hTo24hString, time24hTo12hString } from '../../util/timeStrings'
import { generateShortUUID } from '../../util/id'

// Props
const props = withDefaults(
  defineProps<{
    value?: string
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    variant?: 'blue-gray' | 'default'
  }>(),
  {
    variant: 'default',
  },
)

// Emits
const emit = defineEmits<{
  (e: 'change', value: string | null): void
}>()

// Reactive state
const isDropdownOpen = ref(false)
const uuid = ref('')

// Template refs
const timeInputContainer = ref<HTMLDivElement>()
const timeInputDropdown = ref<HTMLDivElement>()
const timeInputElement = ref<HTMLInputElement>()
const digitalTimePicker = ref<InstanceType<typeof WheelsDigitalTimePicker>>()

// Computed properties
const isoValue = computed(() => {
  return time12hTo24hString(props.value || '')
})

watch(isDropdownOpen, (open) => {
  if (open) {
    nextTick(() => {
      setDropdownMaxHeight()
    })
  }
})

// Lifecycle
onMounted(() => {
  uuid.value = generateShortUUID()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Methods

// TODO: Consider using Vue teleport for the dropdown
function toggleDropdown(): void {
  if (props.disabled) return

  if (!isDropdownOpen.value) {
    isDropdownOpen.value = true
    nextTick(() => setDropdownMaxHeight())
  } else {
    isDropdownOpen.value = false
  }
}

function closeDropdown(): void {
  if (isDropdownOpen.value) {
    isDropdownOpen.value = false
  }
}

// If we're clicking within the dropdown or picker, keep it open
// Otherwise, close it
function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement

  const clickedInsideContainer =
    timeInputContainer.value?.contains(target) ?? false
  const clickedInsideDropdown =
    timeInputDropdown.value?.contains(target) ?? false
  const clickedInsidePicker =
    digitalTimePicker.value?.$el.contains(target) ?? false

  const shouldClose =
    !clickedInsideContainer && !clickedInsideDropdown && !clickedInsidePicker

  if (shouldClose) {
    closeDropdown()
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggleDropdown()
    return
  }
  if (isDropdownOpen.value && event.key === 'Escape') {
    event.preventDefault()
    closeDropdown()
  }
}

function handleDigitalPickerChange(isoTime: string | null): void {
  const displayTime = time24hTo12hString(isoTime || '')
  emit('change', displayTime)
}

/**
 * Dynamically calculates and sets the maximum height of the dropdown
 * so it never overflows the viewport.
 * By measuring the input's position with getBoundingClientRect(),
 * we know exactly how much space is available below it.
 *
 * This ensures the dropdown always fits within the viewport,
 * while still enforcing a reasonable minimum height.
 */
function setDropdownMaxHeight(): void {
  const DROPDOWN_PADDING_PX = 100 // px buffer
  const MIN_DROPDOWN_HEIGHT_PX = 200 // Minimum height of the dropdown
  if (!timeInputDropdown.value || !timeInputContainer.value) return

  const inputRect = timeInputContainer.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const spaceBelow = viewportHeight - inputRect.bottom
  const maxHeight = Math.max(
    spaceBelow - DROPDOWN_PADDING_PX,
    MIN_DROPDOWN_HEIGHT_PX,
  )

  timeInputDropdown.value.style.maxHeight = `${maxHeight}px`
}
</script>

<style scoped lang="scss">
.time-input-wrapper {
  --form-field-accent-color: var(--cu-blue-primary);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
}

.input-label {
  font-weight: var(--font-weight-medium);
  font-size: var(--text-sm);
  color: var(--cu-gray-700, #374151);
}

.time-input-field-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid var(--cu-gray-100);
  border-radius: 8px;
  padding: 0 var(--spacing-3);
  min-height: 48px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover:not(.wheels-form-disabled) {
    border-color: var(--cu-blue-primary);
  }

  &:focus-within {
    border-color: var(--form-field-accent-color);
    outline: none;
  }

  &.wheels-form-variant-blue-gray {
    background: var(--cu-gray-50, #f9fafb);
  }
}

.wheels-form-disabled .time-input-field-wrapper {
  background: var(--cu-gray-100, #f3f4f6);
  border-color: var(--cu-gray-200, #e5e7eb);
  cursor: not-allowed;
}

.wheels-form-error .time-input-field-wrapper {
  border-color: var(--cu-error-primary, #ef4444);
}

.time-input-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--cu-gray-400, #9ca3af);
  margin-right: var(--spacing-2);
}

.time-input-field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--text-lg);
  height: 44px;
  color: var(--cu-gray-600);
  cursor: pointer;

  &::placeholder {
    color: var(--cu-gray-400);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--cu-gray-500);
  }
}

.time-picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  min-width: 320px;
  box-shadow: var(
    --cu-shadow-900,
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04)
  );
  z-index: 1000;
  padding: var(--spacing-4);

  display: none;
  opacity: 0;
  transform: scale(0.95);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;

  &.active {
    display: block;
    opacity: 1;
    transform: scale(1);
  }
}

@starting-style {
  .time-picker-dropdown.active {
    opacity: 0;
    transform: scale(0.95);
  }
}

// Error and disabled states
.wheels-form-error {
  .time-input-icon {
    color: var(--cu-error-primary, #ef4444);
  }
}

.wheels-form-disabled {
  .time-input-icon,
  .time-input-field {
    color: var(--cu-gray-400, #9ca3af);
  }
}
</style>
